import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Observable, Subject } from 'rxjs';

import { UIService } from '@services';
import {
  AuthStore,
  BookingStore,
  FirmwareUpdateStep,
  FirmwareUpdateStore,
  InstallationDetailsStore,
  InstallationFeaturesStore,
  RemoteConfigStore,
  RemoteServiceStore,
  RichDevice,
} from '@store';

import { takeUntil } from 'rxjs/operators';
import { PaidFeaturesEnum } from '../../../../app/shared/pipes/paid-feature-available/paid-feature-available.pipe';
import { InstallationService } from '../../services/installation.service';
import {
  DiagnosticsOverlayTypeEnum,
  FeaturesDetailsOverlayTypeEnum,
  PartnerAccessOverlayTypeEnum,
  RemoteServiceOverlayTypeEnum,
  SingleRoomControlOverlayTypeEnum,
} from '../installation-overlay.model';

@Component({
  selector: 'app-installation-details-menu-bar',
  templateUrl: './installation-details-menu-bar.component.html',
  styleUrls: ['./installation-details-menu-bar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InstallationDetailsMenuBarComponent implements OnInit, OnDestroy {
  readonly PaidFeatures = PaidFeaturesEnum;

  featuresDetailsOverlayTypeEnum = FeaturesDetailsOverlayTypeEnum;
  singleRoomControlOverlayTypeEnum = SingleRoomControlOverlayTypeEnum;
  diagnosticsOverlayTypeEnum = DiagnosticsOverlayTypeEnum;
  partnerAccessOverlayTypeEnum = PartnerAccessOverlayTypeEnum;
  isOpen = false;
  boilerSerial: string;
  isMaintainerWithFreeOrBasicOrPlusPackage = false;
  isPowerLimitationHistoryMenuItemVisible$: Observable<boolean>;

  private ngUnsubscribe$: Subject<void> = new Subject<void>();

  @Input() firstCircuitId: string;
  @Input() installationId: number;
  @Input() gatewaySerial: string;
  @Input() device: RichDevice;

  constructor(
    public installationDetailsStore: InstallationDetailsStore,
    public installationService: InstallationService,
    public uiService: UIService,
    public bookingStore: BookingStore,
    public firmwareUpdateStore: FirmwareUpdateStore,
    public authStore: AuthStore,
    public remoteConfigStore: RemoteConfigStore,
    public installationFeaturesStore: InstallationFeaturesStore,
    private remoteServiceStore: RemoteServiceStore
  ) {}

  ngOnInit(): void {
    this.isPowerLimitationHistoryMenuItemVisible$ = this.installationDetailsStore.isPowerLimitationHistoryMenuItemVisible(
      this.device
    );
    this.installationDetailsStore.device$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((device) => {
        this.boilerSerial = device.boilerSerial;
      });
    this.installationDetailsStore.isMaintainerWithFreeOrBasicOrPlusPackage$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((isMaintainerWithFreeOrBasicOrPlusPackage) => {
        this.isMaintainerWithFreeOrBasicOrPlusPackage = isMaintainerWithFreeOrBasicOrPlusPackage;
      });
  }

  openOverlay(overlayType?: FeaturesDetailsOverlayTypeEnum): void {
    this.uiService.openActiveOverlay(overlayType);
  }

  openPartnerAccessOverlay(overlayType: PartnerAccessOverlayTypeEnum): void {
    this.uiService.openPartnerAccessOverlay(overlayType);
  }

  openDiagnosticsOverlay(overlayType?: DiagnosticsOverlayTypeEnum): void {
    this.uiService.openDiagnosticOverlay(overlayType, this.installationId, this.gatewaySerial);
  }

  openFirmwareUpdateDialog(): void {
    this.firmwareUpdateStore.setStep(FirmwareUpdateStep.START);
    this.firmwareUpdateStore.verifyGateway();
    this.firmwareUpdateStore.openFirmwareUpdateModal(this.gatewaySerial);
  }

  openSingleRoomControlOverlay(overlayType?: SingleRoomControlOverlayTypeEnum): void {
    this.uiService.openSingleRoomControlOverlay(
      overlayType,
      this.installationId,
      this.gatewaySerial
    );
  }

  collapseContentPanel(): void {
    this.isOpen = false;
  }

  openRemoteInspectionOverlay(): void {
    if (this.isMaintainerWithFreeOrBasicOrPlusPackage) {
      this.remoteServiceStore.selectFirstDiagnosisEntry();
      this.uiService.openRemoteServiceOverlay(
        RemoteServiceOverlayTypeEnum.Diagnosis,
        this.installationId,
        this.gatewaySerial
      );
    } else {
      this.remoteServiceStore.selectFirstAnomalyEntry();
      this.uiService.openRemoteServiceOverlay(
        RemoteServiceOverlayTypeEnum.Anomalies,
        this.installationId,
        this.gatewaySerial
      );
    }
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}
